/* Two-step template only. Both purchase cards share one complete-kit offer and checkout flow. */
(() => {
  'use strict';
  if (window.TwoStepPage) { window.TwoStepPage.init(document); return; }
  const controllers = new Set();
  let checkoutBusy = false;
  let checkoutUncertain = false;
  let checkoutNavigating = false;
  const refreshAll = () => controllers.forEach(controller => {
    if (controller.root.isConnected) controller.refresh();
  });
  const motion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  const safeJson = element => { try { return JSON.parse(element.textContent); } catch (_) { return null; } };
  // Calendar midnight in the visitor's local time, recalculated on every tick.
  // This also handles midnight rollover, daylight saving and suspended tabs.
  let countdownInterval = null;
  function refreshCountdowns() {
    const timers = document.querySelectorAll('[data-ts-countdown]');
    if (!timers.length) {
      window.clearInterval(countdownInterval);
      countdownInterval = null;
      return;
    }
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const remaining = Math.max(0, Math.ceil((midnight - now) / 1000));
    const hours = String(Math.floor(remaining / 3600)).padStart(2, '0');
    const minutes = String(Math.floor(remaining % 3600 / 60)).padStart(2, '0');
    const seconds = String(remaining % 60).padStart(2, '0');
    timers.forEach(timer => {
      timer.querySelector('[data-ts-hours]').textContent = hours;
      timer.querySelector('[data-ts-minutes]').textContent = minutes;
      timer.querySelector('[data-ts-seconds]').textContent = seconds;
      timer.querySelector('[role="timer"]').setAttribute('aria-label', `${hours} hours, ${minutes} minutes, ${seconds} seconds until midnight`);
      timer.hidden = false;
    });
  }
  function startCountdowns() {
    refreshCountdowns();
    if (countdownInterval === null && !document.hidden && document.querySelector('[data-ts-countdown]')) {
      countdownInterval = window.setInterval(refreshCountdowns, 1000);
    }
  }
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      window.clearInterval(countdownInterval);
      countdownInterval = null;
    } else startCountdowns();
  });
  function initBuy(root) {
    if (root.dataset.tsReady) return;
    const data = safeJson(root.querySelector('[data-ts-product-data]'));
    if (!data || !data.variants.length) return;
    root.dataset.tsReady = 'true';
    const form = root.querySelector('form');
    const variantInput = form.elements.namedItem('id');
    const planInput = form.elements.namedItem('selling_plan');
    const submit = form.querySelector('[type="submit"]');
    const error = root.querySelector('[data-ts-error]');
    let activeVariant = null;

    let formatter;
    try { formatter = new Intl.NumberFormat(data.locale || 'en', {style:'currency', currency:data.currency}); }
    catch (_) { formatter = new Intl.NumberFormat('en', {style:'currency', currency:data.currency || 'USD'}); }
    const money = value => formatter.format(value / 100);
    const regularPrice = variant => data.useVariantPrice ? variant.compare || variant.price : data.regularPrice;
    const unitPrice = (variant, plan) => plan ? plan.price : data.useVariantPrice ? Math.round(regularPrice(variant) * data.offerRatio) : data.offerPrice;
    function refreshDelivery() {
      root.querySelectorAll('[data-ts-delivery]').forEach(element => {
        const date = new Date();
        date.setDate(date.getDate() + (Number(element.dataset.days) || 3));
        element.textContent = date.toLocaleDateString(data.locale || 'en', {weekday:'long', month:'long', day:'numeric'});
      });
    }
    function refresh(sync) {
      const variant = data.variants.find(v => String(v.id) === String(variantInput.value));
      if (!variant) { submit.disabled = true; return; }
      if (activeVariant !== variant.id) {
        const previousPlan = planInput.value;
        planInput.replaceChildren();
        // Any Revley selling plan on the variant makes this a subscription-only offer.
        if (!data.requiresPlan && !variant.plans.length) planInput.add(new Option('One-time purchase', ''));
        variant.plans.forEach(plan => planInput.add(new Option(plan.name, String(plan.id))));
        if (Array.from(planInput.options).some(o => o.value === previousPlan)) planInput.value = previousPlan;
        activeVariant = variant.id;
      }
      planInput.disabled = !variant.plans.length;
      root.querySelector('.ts-plan-wrap').hidden = variant.plans.length < 2;
      const plan = variant.plans.find(p => String(p.id) === planInput.value) || variant.plans[0];
      if (plan && planInput.value !== String(plan.id)) planInput.value = String(plan.id);
      const price = unitPrice(variant, plan);
      const paidQuantity = Number(form.querySelector('[name="ts_bundle"]:checked').value);
      const totalQuantity = paidQuantity * 2 - 1;
      form.elements.namedItem('quantity').value = String(totalQuantity);
      form.querySelectorAll('[name="ts_bundle"]').forEach(radio => {
        radio.closest('.ts-pack').classList.toggle('is-selected', radio.checked);
      });
      root.querySelectorAll('[data-ts-benefit]').forEach(card => {
        const locked = Number(card.dataset.tsBenefit) > paidQuantity;
        card.classList.toggle('is-locked', locked);
        card.setAttribute('aria-disabled', String(locked));
        card.setAttribute('aria-label', `${card.dataset.benefitName}: ${locked ? `locked. Choose Buy ${card.dataset.tsBenefit} to unlock.` : 'unlocked.'}`);
        card.querySelector('[data-ts-benefit-label]').textContent = locked ? 'Locked' : card.dataset.benefitName;
        card.querySelector('[data-ts-benefit-lock]').hidden = !locked;
      });
      const benefitStatus = root.querySelector('[data-ts-benefit-status]');
      if (benefitStatus) benefitStatus.textContent = paidQuantity === 1 ? 'Free shipping unlocked.' : `Free shipping and ${paidQuantity - 1} free complete ${paidQuantity === 2 ? 'set' : 'sets'} unlocked. ${totalQuantity} complete sets in total.`;
      root.querySelectorAll('[data-ts-each]').forEach(el => {
        const paid = Number(el.dataset.tsEach);
        el.textContent = money(Math.round(price * paid / (paid * 2 - 1)));
      });
      root.querySelectorAll('[data-ts-full-value]').forEach(el => {
        const sets = Number(el.dataset.tsFullValue) * 2 - 1;
        const fullValue = money(regularPrice(variant) * sets);
        el.textContent = fullValue;
        el.setAttribute('aria-label', `Full value for ${sets} complete ${sets === 1 ? 'kit' : 'sets'}: ${fullValue}`);
      });
      const available = variant.available && (!data.requiresPlan || !!plan);
      form.querySelectorAll('[name="ts_bundle"]').forEach(radio => { radio.disabled = checkoutBusy || checkoutUncertain; });
      if (variantInput.tagName === 'SELECT') variantInput.disabled = checkoutBusy || checkoutUncertain;
      if (checkoutBusy || checkoutUncertain) planInput.disabled = true;
      submit.disabled = checkoutBusy || checkoutUncertain || !available;
      submit.setAttribute('aria-busy', String(checkoutBusy));
      root.querySelector('[data-ts-button-label]').textContent = checkoutBusy ? 'Taking you to checkout…' : available ? 'Add to cart' : 'Sold out';
      if (sync) {
        controllers.forEach(other => {
          if (other.root !== root && other.root.isConnected && other.productId === data.productId) other.set(variant.id, paidQuantity, planInput.value);
        });
      }
    }
    const controller = {root, productId:data.productId, refresh:() => refresh(false), refreshDelivery, set(id, quantity, plan) {
      if (!data.variants.some(v => String(v.id) === String(id))) return;
      variantInput.value = String(id);
      const radio = Array.from(form.querySelectorAll('[name="ts_bundle"]')).find(r => Number(r.value) === quantity);
      if (radio) radio.checked = true;
      refresh(false);
      if (Array.from(planInput.options).some(o => o.value === plan)) planInput.value = plan;
      refresh(false);
    }};
    controllers.add(controller);
    form.addEventListener('change', () => { if (!checkoutUncertain) error.hidden = true; refresh(true); });
    refreshDelivery();
    root.querySelectorAll('[data-ts-benefit]').forEach(card => card.addEventListener('click', () => {
      if (card.classList.contains('is-locked') || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      card.querySelector('.ts-confetti')?.remove();
      const burst = document.createElement('span');
      burst.className = 'ts-confetti';
      burst.setAttribute('aria-hidden', 'true');
      for (let index = 0; index < 14; index++) {
        const piece = document.createElement('span');
        const angle = Math.PI * 2 * index / 14;
        const distance = 28 + Math.random() * 30;
        piece.style.setProperty('--dx', `${Math.cos(angle) * distance}px`);
        piece.style.setProperty('--dy', `${Math.sin(angle) * distance - 10}px`);
        piece.style.setProperty('--rot', `${Math.round(Math.random() * 360)}deg`);
        burst.appendChild(piece);
      }
      card.appendChild(burst);
      window.setTimeout(() => burst.remove(), 850);
    }));
    form.addEventListener('submit', async event => {
      event.preventDefault();
      // Keep the theme's delegated cart/drawer handlers away from this form.
      event.stopImmediatePropagation();
      if (checkoutBusy || checkoutUncertain || submit.disabled) return;
      error.hidden = true;
      const paidQuantity = Number(form.querySelector('[name="ts_bundle"]:checked').value);
      if (![1, 2, 3].includes(paidQuantity)) return;
      const totalQuantity = paidQuantity * 2 - 1;
      const variant = data.variants.find(v => String(v.id) === String(variantInput.value));
      const plan = variant.plans.find(p => String(p.id) === planInput.value) || variant.plans[0];
      const selectedPlan = plan ? String(plan.id) : '';
      const expectedTotal = unitPrice(variant, plan) * paidQuantity;
      const freeQuantity = data.freeVariantId ? paidQuantity - 1 : 0;
      const paidLineQuantity = totalQuantity - freeQuantity;
      checkoutBusy = true;
      refreshAll();
      // A private marker lets us verify and, if needed, roll back only this
      // click's items. Existing items in the customer's cart are preserved.
      const offerId = `ts-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      // Paid kits go on the selling plan (subscription). Free kits are added as
      // the one-time "Free Kit" variant so they never carry into rebills;
      // Shopify's matching Buy X Get Y discount makes them free.
      const item = {id:variantInput.value, quantity:paidLineQuantity, properties:{_ts_offer:offerId}};
      if (selectedPlan) item.selling_plan = selectedPlan;
      const items = [item];
      if (freeQuantity > 0) items.push({id:String(data.freeVariantId), quantity:freeQuantity, properties:{_ts_offer:offerId}});
      const offerVariantIds = new Set(items.map(entry => String(entry.id)));
      const lineVariantId = line => String(line.variant_id || line.id);
      const linePlanId = line => String(line.selling_plan_allocation?.selling_plan?.id ?? '');
      let safelyReverted = false;
      async function readCart() {
        const response = await fetch(data.cartUrl, {credentials:'same-origin', cache:'no-store', headers:{'Accept':'application/json'}});
        if (!response.ok) throw new Error('cart-reconciliation-unavailable');
        const cart = await response.json();
        if (!Array.isArray(cart.items)) throw new Error('cart-reconciliation-unavailable');
        return cart;
      }
      async function revertOffer(cart) {
        const offerLines = cart.items.filter(line => line.properties?._ts_offer === offerId);
        if (!offerLines.length) return true;
        if (offerLines.some(line => !line.key)) throw new Error('cart-reconciliation-unavailable');
        const updates = {};
        offerLines.forEach(line => { updates[line.key] = 0; });
        const response = await fetch(data.updateUrl, {
          method:'POST', credentials:'same-origin',
          headers:{'Content-Type':'application/json','Accept':'application/json'},
          body:JSON.stringify({updates})
        });
        const reverted = await response.json().catch(() => ({}));
        return response.ok && Array.isArray(reverted.items) && !reverted.items.some(line => line.properties?._ts_offer === offerId);
      }
      try {
        const response = await fetch(data.addUrl, {
          method:'POST', credentials:'same-origin',
          headers:{'Content-Type':'application/json','Accept':'application/json'},
          body:JSON.stringify({items})
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          // Shopify can add the remaining inventory even while returning 422.
          // Reconcile every rejected add before allowing another submission.
          safelyReverted = await revertOffer(await readCart());
          if (!safelyReverted) throw new Error('cart-reconciliation-unavailable');
          throw new Error(typeof result.description === 'string' ? result.description : 'This selection could not be added. Please check availability and try again.');
        }
        if (data.discountCode && data.discountCode.trim()) {
          const discounted = await fetch(data.updateUrl, {
            method:'POST', credentials:'same-origin',
            headers:{'Content-Type':'application/json','Accept':'application/json'},
            body:JSON.stringify({discount:data.discountCode.trim()})
          });
          if (!discounted.ok) throw new Error('The offer could not be applied. Please try again.');
        }
        // Shopify can spread a discount over a combined line or split the
        // same variant into paid/free lines. Verify their combined quantity
        // and subtotal instead of requiring any individual line to be free.
        const cart = await readCart();
        const offerLines = cart.items.filter(line => line.properties?._ts_offer === offerId);
        const paidLines = offerLines.filter(line => lineVariantId(line) === String(item.id));
        const validLines = offerLines.length > 0 && offerLines.every(line =>
          offerVariantIds.has(lineVariantId(line)) &&
          Number.isInteger(line.quantity) && line.quantity > 0 &&
          Number.isFinite(line.final_line_price) && line.final_line_price >= 0
        ) && paidLines.reduce((sum, line) => sum + line.quantity, 0) === paidLineQuantity &&
          paidLines.every(line => linePlanId(line) === selectedPlan);
        const receivedQuantity = offerLines.reduce((sum, line) => sum + line.quantity, 0);
        let offerTotal = offerLines.reduce((sum, line) => sum + line.final_line_price, 0);
        // An order-level first-order discount is separate from Ajax cart line
        // prices. Read its real allocations instead of guessing a percentage
        // or subtracting a discount belonging to another cart item.
        if (validLines && receivedQuantity === totalQuantity && offerTotal > expectedTotal && cart.cart_level_discount_applications?.length) {
          const stateResponse = await fetch(data.cartStateUrl, {credentials:'same-origin', cache:'no-store', headers:{'Accept':'text/html'}});
          if (!stateResponse.ok) throw new Error('The first-order offer could not be confirmed. Please try again.');
          const stateDocument = new DOMParser().parseFromString(await stateResponse.text(), 'text/html');
          const stateElement = stateDocument.querySelector('[data-ts-cart-state]');
          const state = stateElement && safeJson(stateElement);
          const pricedLines = (state?.items || []).filter(line => line.offer_id === offerId);
          if (pricedLines.length && pricedLines.every(line =>
            offerVariantIds.has(String(line.variant_id)) &&
            Number.isInteger(line.quantity) && line.quantity > 0 &&
            Number.isFinite(line.payable) && line.payable >= 0
          ) && pricedLines.reduce((sum, line) => sum + line.quantity, 0) === totalQuantity) {
            offerTotal = pricedLines.reduce((sum, line) => sum + line.payable, 0);
          }
        }
        if (!validLines || receivedQuantity !== totalQuantity || offerTotal > expectedTotal) {
          safelyReverted = await revertOffer(cart);
          if (!safelyReverted) throw new Error('cart-reconciliation-unavailable');
          throw new Error('This offer could not be confirmed at the displayed price. The items just added have been removed. Please contact us for help.');
        }
        checkoutNavigating = true;
        // Hand off to the Revley checkout embed when it is installed; it
        // exposes its checkout-URL interceptor as window.__maybeRedirect.
        if (!(typeof window.__maybeRedirect === 'function' && window.__maybeRedirect(data.checkoutUrl))) {
          window.location.assign(data.checkoutUrl);
        }
      } catch (failure) {
        if (!safelyReverted) {
          try { safelyReverted = await revertOffer(await readCart()); }
          catch (_) { safelyReverted = false; }
        }
        checkoutNavigating = false;
        checkoutUncertain = !safelyReverted;
        error.textContent = checkoutUncertain
          ? 'We could not confirm your complete offer. Please check your cart before trying again so the same selection is not added twice.'
          : failure.message;
        error.hidden = false;
        checkoutBusy = false;
        refreshAll();
      }
    });
    refresh(false);
  }
  function initGallery(root) {
    if (root.dataset.tsReady) return;
    root.dataset.tsReady = 'true';
    const track = root.querySelector('.ts-gallery-track');
    const slides = Array.from(track.children);
    const dots = Array.from(root.querySelectorAll('[data-ts-slide]'));
    let current = 0;
    const go = index => {
      const selected = Math.max(0, Math.min(slides.length - 1, index));
      if (slides[selected]) track.scrollTo({left:slides[selected].offsetLeft - slides[0].offsetLeft, behavior:motion()});
    };
    dots.forEach((dot, index) => dot.addEventListener('click', () => go(index)));
    root.querySelectorAll('[data-ts-gallery-step]').forEach(button => button.addEventListener('click', () => go((current + Number(button.dataset.tsGalleryStep) + slides.length) % slides.length)));
    track.addEventListener('keydown', event => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); go(current + (event.key === 'ArrowRight' ? 1 : -1)); }
    });
    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        current = slides.reduce((closest, slide, index) => Math.abs(slide.offsetLeft - slides[0].offsetLeft - track.scrollLeft) < Math.abs(slides[closest].offsetLeft - slides[0].offsetLeft - track.scrollLeft) ? index : closest, 0);
        dots.forEach((dot,index) => { if (index === current) dot.setAttribute('aria-current','true'); else dot.removeAttribute('aria-current'); });
        const counter = root.querySelector('[data-ts-gallery-count]');
        if (counter) counter.textContent = `${current + 1} / ${slides.length}`;
        ticking = false;
      });
    }, {passive:true});
  }
  function init(scope) {
    startCountdowns();
    controllers.forEach(controller => { if (!controller.root.isConnected) controllers.delete(controller); });
    scope.querySelectorAll('[data-ts-buy]').forEach(initBuy);
    scope.querySelectorAll('[data-ts-gallery]').forEach(initGallery);
    scope.querySelectorAll('.ts-page').forEach(page => {
      if (page.dataset.stickyReady || !('IntersectionObserver' in window)) return;
      page.dataset.stickyReady = 'true';
      const sticky = page.querySelector('.ts-sticky');
      const topOffer = page.querySelector('#two-step-offers');
      const bottomOffer = page.querySelector('#two-step-final-offers');
      if (!sticky || !topOffer) return;
      let topPassed = false;
      let bottomVisible = false;
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.target === topOffer) topPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          if (entry.target === bottomOffer) bottomVisible = entry.isIntersecting;
        });
        sticky.classList.toggle('is-visible', topPassed && !bottomVisible);
      });
      observer.observe(topOffer);
      if (bottomOffer) observer.observe(bottomOffer);
      page.addEventListener('shopify:section:unload', () => observer.disconnect(), {once:true});
    });
    scope.querySelectorAll('[data-ts-carousel]').forEach(root => {
      if (root.dataset.tsReady) return;
      root.dataset.tsReady = 'true';
      const track = root.querySelector('.ts-carousel-track');
      root.querySelectorAll('[data-ts-step]').forEach(button => button.addEventListener('click', () => {
        const first = track.firstElementChild;
        if (first) track.scrollBy({left: Number(button.dataset.tsStep) * (first.getBoundingClientRect().width + 16), behavior:motion()});
      }));
    });
  }
  // Browsers can restore this page, including disabled form controls, after
  // Back from checkout. Only clear the busy state for a completed navigation;
  // an unresolved cart mutation must remain locked against duplicate adds.
  window.addEventListener('pageshow', event => {
    startCountdowns();
    if (!event.persisted) return;
    if (checkoutNavigating && !checkoutUncertain) {
      checkoutNavigating = false;
      checkoutBusy = false;
    }
    controllers.forEach(controller => {
      if (controller.root.isConnected) controller.refreshDelivery();
    });
    refreshAll();
  });
  window.TwoStepPage = {init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), {once:true});
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', () => { controllers.forEach(c => { if (!c.root.isConnected) controllers.delete(c); }); });
})();
